/* Discovery events share the consent-gated storefront publisher. Standard
   Shopify search/collection events remain owned by the installed app pixels. */
(() => {
  if (window.AlAsilDiscovery) return;
  const emit = (event_name, data = {}, options) => {
    if (typeof window.publishAlasilEvent !== 'function') return false;
    try { return window.publishAlasilEvent('alasil:storefront_interaction', { event_name, currency: window.Shopify?.currency?.active, ...data }, options); } catch (_) { return false; }
  };
  const listFor = (card) => card.closest('[data-item-list-name], #product-grid, product-recommendations, .shopify-section') || card.parentElement;
  const listName = (list) => list?.dataset.itemListName || (list?.id === 'product-grid' ? (/\/search$/.test(location.pathname) ? 'search_results' : 'collection') : list?.id || 'recommendations');
  const itemFor = (card, index) => {
    const product_id = card.dataset.productId;
    const variant_id = card.querySelector('.product-variant-id')?.value || card.dataset.analyticsVariantId || card.dataset.variantId;
    const country = window.Shopify?.country;
    return {
      product_id, variant_id,
      ...(country && product_id && variant_id ? { item_id: `shopify_${country}_${product_id}_${variant_id}` } : {}),
      item_name: card.dataset.analyticsProductName,
      price: Number(card.dataset.analyticsPrice) || 0,
      index,
    };
  };
  const cardsIn = (root) => Array.from(root?.querySelectorAll('.product-card-wrapper[data-product-id]') || []);
  const promotionData = (node, link) => {
    const data = { promotion_id: node.dataset.promotionId, promotion_name: node.dataset.promotionName, creative_slot: node.dataset.creativeSlot };
    const product_id = node.dataset.promotionProductId, variant_id = node.dataset.promotionVariantId;
    let productDestination = !link;
    if (link && node.dataset.promotionProductUrl) {
      try { productDestination = link.pathname === new URL(node.dataset.promotionProductUrl, location.origin).pathname; } catch (_) {}
    }
    if (productDestination && product_id && variant_id) data.items = [{
      product_id, variant_id, item_name: node.dataset.promotionProductName,
      ...(window.Shopify?.country ? { item_id: `shopify_${window.Shopify.country}_${product_id}_${variant_id}` } : {}),
      ...(node.dataset.promotionPrice && Number.isFinite(Number(node.dataset.promotionPrice)) ? { price: Number(node.dataset.promotionPrice) } : {}), quantity: 1,
    }];
    return data;
  };
  const observed = new WeakSet();
  const reported = new WeakSet();
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.visibilityState === 'hidden') continue;
      // Keep observing when consent/publisher is unavailable. No pre-consent
      // interaction is queued; a later visible impression may be measured.
      if (emit('view_promotion', promotionData(entry.target)) === true) {
        reported.add(entry.target);
        observer.unobserve(entry.target);
      }
    }
  }, { threshold: 0.5 }) : null;
  function observePromotions(root = document) {
    root.querySelectorAll('[data-promotion-id]').forEach((node) => {
      if (observed.has(node)) return;
      observed.add(node);
      observer?.observe(node);
    });
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented) return;
    const navigation = window.AlAsilAnalyticsNavigation?.(event, link);
    const promotion = link.closest('[data-promotion-id]');
    if (promotion) emit('select_promotion', promotionData(promotion, link), navigation);
    const card = link.closest('.product-card-wrapper[data-product-id]');
    if (!card || !/\/products\//.test(link.pathname)) return;
    const predictive = card.closest('predictive-search, #SearchSheet');
    const list = predictive || listFor(card);
    const index = cardsIn(list).indexOf(card);
    emit('select_item', {
      item_list_name: predictive ? 'search_suggest' : listName(list),
      ...(predictive ? { search_term: predictive.getQuery?.() || predictive.querySelector('[data-ss-input]')?.value?.trim(), search_type: 'suggestion' } : {}),
      index, items: [itemFor(card, index)],
    }, navigation);
  });
  function loaded(items, grid, automatic) {
    const cards = items.flatMap((item) => cardsIn(item));
    emit('load_more', { items_count: cards.length, load_type: automatic ? 'automatic' : 'button', item_list_name: listName(grid) });
    if (!cards.length) return;
    const all = cardsIn(grid);
    emit('view_item_list', { item_list_name: listName(grid), list_source: 'load_more', items: cards.map((card) => itemFor(card, all.indexOf(card))) });
  }
  function facetChanges(before, after) {
    const previous = new URLSearchParams(before), next = new URLSearchParams(after), changes = [];
    const controls = Array.from(document.querySelectorAll('facet-filters-form input[name], facet-filters-form select[name]'));
    const readable = (name, value) => {
      const control = controls.find((input) => input.name === name && input.value === value);
      const label = control?.closest('label') || control?.labels?.[0];
      const detail = control?.closest('details');
      const title = detail?.querySelector('.facets__summary-label, summary span');
      return {
        filter_name: title?.textContent?.trim() || name.replace(/^filter\.[vp]\.(m\.)?/, ''),
        filter_value: control?.dataset.analyticsValue || control?.getAttribute('aria-label') || label?.querySelector('.facet-checkbox__text')?.textContent?.trim().replace(/\s*\(\d+\)\s*$/, '') || label?.textContent?.trim().replace(/\s*\(\d+\)\s*$/, '') || (/gid:\/\//.test(value) ? 'unknown' : value),
      };
    };
    const defaultSort = document.querySelector('[data-default-sort]')?.dataset.defaultSort ||
      controls.find((control) => control.name === 'sort_by' && control.tagName === 'SELECT')?.querySelector('option[selected]')?.value || 'manual';
    if ((previous.get('sort_by') || defaultSort) !== (next.get('sort_by') || defaultSort)) changes.push({ event_name: 'sort_changed', sort_by: next.get('sort_by') || defaultSort });
    for (const name of new Set([...previous.keys(), ...next.keys()])) {
      if (!name.startsWith('filter.')) continue;
      for (const value of next.getAll(name)) if (!previous.getAll(name).includes(value)) changes.push({ event_name: 'filter_applied', ...readable(name, value) });
      for (const value of previous.getAll(name)) if (!next.getAll(name).includes(value)) changes.push({ event_name: 'filter_removed', ...readable(name, value) });
    }
    return changes;
  }
  const reportSearchResults = () => {
    const results = document.querySelector('[data-search-results-count]');
    if (!results || results.dataset.analyticsReported || document.visibilityState === 'hidden') return;
    if (emit('search_results_viewed', { search_term: results.dataset.searchTerm, results_count: Number(results.dataset.searchResultsCount), search_type: 'results_page' }) !== false) results.dataset.analyticsReported = 'true';
  };
  window.AlAsilDiscovery = { emit, loaded, facetChanges, observePromotions };
  const ready = () => { observePromotions(); reportSearchResults(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  document.addEventListener('shopify:section:load', (event) => observePromotions(event.target));
  const refreshVisible = () => {
    document.querySelectorAll('[data-promotion-id]').forEach((node) => {
      if (reported.has(node)) return;
      observer?.unobserve(node);
      observer?.observe(node);
    });
    reportSearchResults();
  };
  // An already-consented visitor may load this asset before Shopify's privacy
  // SDK finishes. SDK readiness does not emit visitorConsentCollected. Recheck
  // only currently visible impressions/results; never replay earlier clicks.
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) refreshVisible();
      });
    } catch (_) { /* Consent remains fail-closed if the SDK cannot load. */ }
  }
  document.addEventListener('visitorConsentCollected', refreshVisible);
  document.addEventListener('alasil:google-analytics-ready', refreshVisible);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshVisible(); });
})();
